/**
 * The route table.
 *
 * ## Why the lazy pages are loaded through `import.meta.glob`
 *
 * The tuning screen and the sketchbook pages are owned by other chunks and land on their
 * own schedule. A bare `lazy(() => import('./pages/TuningPage.js'))` makes this file
 * fail to typecheck until that file exists, which would couple the chunks' merge order
 * for no benefit. `import.meta.glob` is resolved by Vite at transform time and typed by
 * `vite/client` as a plain record, so a missing page is a *runtime* absence — handled
 * below with a placeholder — rather than a build error.
 *
 * The glob names the files explicitly rather than using `./pages/*.tsx`, so that
 * `SessionPage`, which is imported eagerly because it is the app, does not also get split
 * into its own chunk.
 *
 * Routes are exported as a `RouteObject[]` and rendered through `useRoutes`, so tests can
 * mount them under a `MemoryRouter` without the app supplying a browser history.
 */

import { Suspense, lazy, type ComponentType } from 'react';
import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';
import { AppFrame } from './components/system/AppFrame.js';
import { SessionPage } from './pages/SessionPage.js';

type PageModule = { default: ComponentType };

const externalPages = import.meta.glob<PageModule>(
  './pages/{TuningPage,SketchbookPage,SketchDetailPage}.tsx',
);

/**
 * A route whose page belongs to another chunk.
 *
 * Renders a plain note rather than throwing when the page has not landed yet — an
 * unbuilt route should be an obvious gap, not a blank screen with a console error.
 */
function lazyExternalPage(file: string, label: string): ComponentType {
  return lazy(async (): Promise<PageModule> => {
    const loader = externalPages[`./pages/${file}.tsx`];
    if (!loader) {
      return {
        default: () => (
          <section className="hc-screen" data-testid="hc-missing-page">
            <h1 className="hc-screen__title">{label}</h1>
            <p className="hc-screen__lead">This screen has not been built yet.</p>
          </section>
        ),
      };
    }
    return loader();
  });
}

const TuningPage = lazyExternalPage('TuningPage', 'Recogniser tuning');
const SketchbookPage = lazyExternalPage('SketchbookPage', 'Sketchbook');
const SketchDetailPage = lazyExternalPage('SketchDetailPage', 'Sketch');
const DevStateSwitcher = lazy(() => import('./components/system/DevStateSwitcher.js'));

/** Deliberately almost invisible: route transitions here should not read as loading. */
function RouteFallback(): React.JSX.Element {
  return <div className="hc-route-fallback" aria-hidden="true" />;
}

/**
 * The sketchbook, inside the frame that owns the type scale.
 *
 * `SessionPage` builds its own `AppFrame` on all four of its returns, so these two pages
 * were the only screens in the app rendered straight into `#root` — which meant they read
 * the *fallback* sizes in `tokens.css` and answered nothing about the viewport at all. It
 * did not show while those fallbacks were an iPad's, and it showed the moment a phone got
 * its own scale: a list you reach from a session in 14 px type, rendering at 17. Wrapped
 * here rather than inside each page because `SketchDetailPage` returns from three places
 * and none of them is about the frame.
 */
function Framed({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <AppFrame className="hc-app--screen">{children}</AppFrame>;
}

export const routes: RouteObject[] = [
  { path: '/', element: <SessionPage /> },
  {
    path: '/sketchbook',
    element: (
      <Framed>
        <SketchbookPage />
      </Framed>
    ),
  },
  {
    path: '/sketchbook/:sketchId',
    element: (
      <Framed>
        <SketchDetailPage />
      </Framed>
    ),
  },
  { path: '/dev/tuning', element: <TuningPage /> },
  { path: '/dev/states', element: <DevStateSwitcher /> },
  { path: '*', element: <Navigate to="/" replace /> },
];

export function AppRoutes(): React.JSX.Element {
  const element = useRoutes(routes);
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}
